import React from "react";
import Table from "react-bootstrap/Table";
import Pagination from "react-bootstrap/Pagination";
import moment from "moment";
import { Copy } from "lucide-react";
import { toast } from "react-toastify";

const PRODUCT_BADGE = {
  insurance: { label: "Insurance Project", className: "product-badge-insurance" },
  skor_protect: { label: "Skor Protect", className: "product-badge-skor-protect" },
};

// raw_status values are product-specific: insurance -> failed | contracted | awarded | confirmed,
// skor_protect -> waiting_for_payment | waiting_for_rental_contract
// "confirmed" is treated as a synonym of "awarded" (same label, same green badge)
const INSURANCE_STATUS_LABELS = {
  insurance: {
    failed: "Failed",
    contracted: "Contracted",
    awarded: "Awarded",
    confirmed: "Awarded",
  },
  skor_protect: {
    waiting_for_payment: "Awaiting for payment",
    waiting_for_rental_contract: "Waiting for rental contract",
  },
};

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

const getInsuranceStatusLabel = (item) => {
  const label = INSURANCE_STATUS_LABELS[item.product]?.[item.raw_status];
  if (label) return label;
  if (!item.raw_status) return "N/A";
  return item.raw_status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const getInsuranceStatusBadgeClass = (item) => {
  if (item.product === "insurance") {
    if (item.raw_status === "awarded" || item.raw_status === "confirmed") return "status-badge-active";
    if (item.raw_status === "failed") return "status-badge-deleted";
    if (item.raw_status === "contracted") return "insurance-payment-badge direct-debit";
  }
  if (item.product === "skor_protect") {
    if (item.raw_status === "waiting_for_payment") return "status-badge-expired";
    if (item.raw_status === "waiting_for_rental_contract") return "insurance-payment-badge direct-debit";
  }
  return "insurance-payment-badge";
};

const InsuranceProtectTable = ({ data, total, currentPage, limit, onPageChange }) => {
  const totalPages = Math.ceil(total / limit);
  const indexOfFirstItem = (currentPage - 1) * limit + 1;
  const indexOfLastItem = Math.min(currentPage * limit, total);

  const formatProperty = (property) => {
    if (!property) return "N/A";
    return (
      [property.street, property.street_number, property.muncipality]
        .filter(Boolean)
        .join(", ") || "N/A"
    );
  };

  const handleCopyPropertyId = async (propertyId) => {
    if (!propertyId) return;
    try {
      await navigator.clipboard.writeText(propertyId);
      toast.success("Property ID copied");
    } catch (err) {
      toast.error("Failed to copy property ID");
    }
  };

  const renderPagination = () => {
    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    items.push(
      <Pagination.First key="first" onClick={() => onPageChange(1)} disabled={currentPage === 1} />
    );
    items.push(
      <Pagination.Prev key="prev" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} />
    );
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item key={page} active={page === currentPage} onClick={() => onPageChange(page)}>
          {page}
        </Pagination.Item>
      );
    }
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages || totalPages === 0}
      />
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted small">
          Showing <strong>{total > 0 ? indexOfFirstItem : 0}</strong> to{" "}
          <strong>{indexOfLastItem}</strong> of <strong>{total}</strong> records
        </div>
        <Pagination className="tenants-pagination mb-0">{items}</Pagination>
      </div>
    );
  };

  return (
    <>
      <div className="table-responsive">
        <Table hover className="landlord-modal-table insurance-protect-table mb-0">
          <thead>
            <tr>
              <th style={{ width: "32px" }}>#</th>
              <th>PRODUCT</th>
              <th style={{ maxWidth: "180px" }}>PROPERTY</th>
              <th style={{ maxWidth: "110px" }}>TENANTS</th>
              <th style={{ maxWidth: "100px" }}>WORKSPACE</th>
              <th>BENEFICIARY</th>
              <th>POLICY DATE</th>
              <th>PREMIUM</th>
              <th>PAYMENT STATUS</th>
              <th>INSURANCE STATUS</th>
              <th className="d-none">PROPERTY ID</th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((item, index) => {
                const product = PRODUCT_BADGE[item.product] || {};
                const tenantNames = (item.tenants || [])
                  .map((t) => `${t.fname || ""} ${t.lname || ""}`.trim())
                  .filter(Boolean);
                const isPaid = item.payment_status === "paid";

                return (
                  <tr key={item._id || index}>
                    <td className="text-muted" style={{ width: "32px" }}>
                      {indexOfFirstItem + index}
                    </td>
                    <td>
                      <span className={`product-badge ${product.className || ""}`}>
                        {product.label || "N/A"}
                      </span>
                    </td>
                    <td
                      className="fw-600"
                      style={{ maxWidth: "180px", fontSize: "0.8rem", lineHeight: "1.4", wordBreak: "break-word" }}
                    >
                      {formatProperty(item.property)}
                    </td>
                    <td style={{ maxWidth: "110px" }}>
                      <div className="fw-600" style={{ fontSize: "0.8rem", lineHeight: "1.4", wordBreak: "break-word" }}>
                        {tenantNames.length > 0
                          ? tenantNames.map((name) => <div key={name}>{name}</div>)
                          : "N/A"}
                      </div>
                    </td>
                    <td
                      className="fw-600"
                      style={{ maxWidth: "100px", fontSize: "0.8rem", lineHeight: "1.4", wordBreak: "break-word" }}
                    >
                      {item.workspace?.name || "N/A"}
                    </td>
                    <td>
                      <div className="fw-600">{capitalize(item.beneficiary?.type) || "N/A"}</div>
                      {item.beneficiary?.name && (
                        <div className="text-muted small">{item.beneficiary.name}</div>
                      )}
                    </td>
                    <td className="text-muted small fw-medium">
                      {item.policy_date ? moment(item.policy_date).format("DD MMM YYYY") : "N/A"}
                    </td>
                    <td className="fw-600">
                      {item.premium != null ? `€ ${Number(item.premium).toLocaleString()}` : "N/A"}
                    </td>
                    <td>
                      <span className={isPaid ? "status-badge-active" : "status-badge-not-paid"}>
                        {isPaid ? "Paid" : "Not paid"}
                      </span>
                    </td>
                    <td>
                      <span className={getInsuranceStatusBadgeClass(item)}>
                        {getInsuranceStatusLabel(item)}
                      </span>
                    </td>
                    <td>
                      {item.property?._id && (
                        <button
                          type="button"
                          className="ip-copy-btn"
                          title="Copy property ID"
                          onClick={() => handleCopyPropertyId(item.property._id)}
                        >
                          <Copy size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={11} className="text-center py-5 text-muted">
                  No results found matching your filters
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {totalPages > 1 && total > 0 && <div className="px-1">{renderPagination()}</div>}
    </>
  );
};

export default InsuranceProtectTable;
